import {
  HTTPForbiddenError,
  HTTPNotFoundError,
  HTTPResponseError,
  HTTPUnauthorizedError,
  HTTPUtils,
  type BaseErrorSubClassInstanceProps,
  type HTTPResponseErrorProps,
} from "@validereinc/utilities";
import { type RequestWrapperType, requestWrapper } from "./requestWrapper.ts";
import { type RequestCallbackType } from "./types.ts";

export type DELETERequestType = { version?: number } & Pick<
  RequestWrapperType,
  "endpoint" | "headers" | "responseType"
>;

type DELETEProps = {
  api: string;
  requestCallback?: RequestCallbackType;
} & DELETERequestType;

/**
 * Make an HTTP DELETE request. Default response parsing is json. If you need the
 * response object directly, use fetch() instead.
 *
 * @param props - DELETE request props
 * @returns response body as T
 */
export const DELETE = async <T = unknown>({
  api,
  endpoint,
  headers = {},
  responseType = "json",
  requestCallback = (response) => response,
}: DELETEProps): Promise<T> => {
  try {
    const request = requestWrapper({
      method: "DELETE",
      endpoint: `${api}${endpoint}`,
      headers,
    });
    const response = requestCallback
      ? await fetch(request).then(requestCallback)
      : await fetch(request);

    if (HTTPUtils.status.isSuccess(response.status)) {
      switch (responseType) {
        case "arraybuffer":
          return (await response.arrayBuffer()) as T;
        case "blob":
          return (await response.blob()) as T;
        case "json": {
          // Some successful 20X responses from the backend will contain No
          // content. Calling response.json() will throw an exception even
          // though the response succeeded. So we check if response has content
          // before parsing JSON. Note, we are doing this for DELETE only
          // intentionally.
          const contentLength = response.headers.get("Content-Length");
          const hasContent = contentLength !== "0" && contentLength !== null;

          // For 204 No Content, we know there's no body
          if (response.status === 204 || !hasContent) {
            return {} as T;
          }

          return (await response.json()) as T;
        }
        default:
          return {} as T;
      }
    } else {
      throw { response, request };
    }
  } catch (cause: unknown) {
    if (!HTTPUtils.objectHasResponseAndRequest(cause)) {
      throw new HTTPResponseError(
        "DELETE fetch failure or request utility error:",
        {
          method: "DELETE",
          endpoint: `${api}${endpoint}`,
          cause,
        }
      );
    }

    // cause excluded since it is a custom object we constructed
    const props: BaseErrorSubClassInstanceProps<HTTPResponseErrorProps> = {
      method: "DELETE",
      status: cause.response.status,
      endpoint: cause.request.url,
      responseContentType: cause.response.headers.get("Content-Type"),
    };

    if (
      cause.response.headers.get("Content-Type")?.includes("application/json")
    ) {
      if (HTTPUtils.status.isNotAuthorized(cause.response.status)) {
        throw new HTTPUnauthorizedError("DELETE unauthorized", {
          ...props,
          responseBodyJson: await cause.response.json(),
        });
      } else if (HTTPUtils.status.isForbidden(cause.response.status)) {
        throw new HTTPForbiddenError("DELETE forbidden", {
          ...props,
          responseBodyJson: await cause.response.json(),
        });
      } else if (HTTPUtils.status.isNotFound(cause.response.status)) {
        throw new HTTPNotFoundError("DELETE not found", {
          ...props,
          responseBodyJson: await cause.response.json(),
        });
      } else {
        throw new HTTPResponseError("DELETE error with info from API", {
          ...props,
          responseBodyJson: await cause.response.json(),
        });
      }
    }

    switch (cause.response.status) {
      case 401:
        throw new HTTPUnauthorizedError("DELETE unauthorized", props);
      case 403:
        throw new HTTPForbiddenError("DELETE forbidden", props);
      case 404:
        throw new HTTPNotFoundError("DELETE not found", props);
      default:
        throw new HTTPResponseError("DELETE unknown error", props);
    }
  }
};
